import React from 'react';
import {
    VStack,
    Button,
    Avatar,
    Text,
    Divider,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure
} from '@chakra-ui/react';

import Const from '../Constants';

import { actualSizePhotoUrl, handleSubscribe, showSuccessToast } from '../Utils';
import { startProductTour } from '../Onboarding/productTour';
import SettingsSection from '../Components/SettingsSection';
import SettingsRow from '../Components/SettingsRow';

const Profile = ({ auth, setAppMode, setIsSignedIn, subscription, setSubscription, toast }) => {
    // eslint-disable-next-line
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = React.useRef();

    // Sign out logic
    async function handleClickSignOut() {
        await auth.signOut();
        await setIsSignedIn(false);
        await setAppMode(Const.sSignIn);
    }

    async function handleCancelSubscription() {
        try {
            const idToken = await auth.currentUser.getIdToken();
            const theURL = process.env.REACT_APP_BASE_URL + '/subscriptions/cancel';
            const response = await fetch(theURL, {
              method: Const.HttpPost,
              headers: new Headers({
                Authorization: idToken,
                Accept: Const.AppJson,
                [Const.ContentType]: Const.AppJson
              })
            });

            // Check if the request was successful
            if(response.ok) {
                setSubscription({
                    state: false,
                    subscriptionId: null, // unused in front end for now
                    timestamp: null // unused in front end for now
                });

                showSuccessToast(toast, `${Const.sAppName} Pro`, 'Subscription is canceled', 'warning');
            }
            else {
                showSuccessToast(toast, `${Const.sAppName} Pro`, 'Failed to cancel the subscription', 'error');
            }
          } catch (error) {
            console.error(error.message);
          }

          // Close the unsubscribe alert dialog
          onClose();
    }

    return (
        <>
        <VStack
            padding={Const.SpaceLG}
            spacing={Const.SpaceLG}
            w={'100%'}
            maxW={'520px'}
            mx={'auto'}
            align={'stretch'}
        >
            {/* Header */}
            <VStack spacing={Const.SpaceXS} align={Const.sCenter}>
                <Avatar
                    src={(auth && auth.currentUser) ? actualSizePhotoUrl(auth.currentUser.photoURL) : ''}
                    size={Const.XL}
                    borderWidth={2}
                />
                <Text fontSize={Const.XL} fontWeight={Const.Bold} color={Const.White}>
                    {auth && auth.currentUser && auth.currentUser.displayName}
                </Text>
                <Text fontSize={Const.XS} fontWeight={Const.Bold} color={Const.ActiveLightColor}>
                    {auth && auth.currentUser && auth.currentUser.email}
                </Text>
            </VStack>

            {/* Account */}
            <SettingsSection label={'Account'}>
                <SettingsRow label={'Plan'} description={'Your current subscription'}>
                    <Text fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray180}>
                        {subscription.state === true ? 'Pro' : 'Free'}
                    </Text>
                </SettingsRow>
                {
                    false && // TODO: Remove the boolean when subscription would be enabled
                    <SettingsRow
                        label={'Subscription'}
                        description={subscription.state === true ? 'Manage your Pro plan' : 'Upgrade to Pro'}
                    >
                        <Button
                            size={Const.SM}
                            height={'30px'}
                            background={ subscription.state === false ? Const.ActiveColor : Const.Gray36 }
                            color={ subscription.state === false ? Const.White : Const.Gray165 }
                            _hover={{
                                background: subscription.state === false ? Const.DoneLightColor : Const.Red600
                            }}
                            fontSize={subscription.state === false ? Const.SM : Const.XS}
                            onClick={() => {
                                if(subscription.state === false) {
                                    handleSubscribe(auth.currentUser.uid);
                                }
                                else {
                                    // Open cancel subscription dialog
                                    onOpen();
                                }
                            }}
                        >
                            { subscription.state === false ? 'Subscribe' : 'Cancel Pro' }
                        </Button>
                    </SettingsRow>
                }
            </SettingsSection>

            {/* Preferences — placeholder for future user configuration */}
            {
                false && // TODO: enable once we add user-configurable preferences
                <SettingsSection label={'Preferences'}>
                    <Text fontSize={Const.SM} color={Const.Gray165}>
                        More settings coming soon.
                    </Text>
                </SettingsSection>
            }

            {/* Help */}
            <SettingsSection label={'Help'}>
                <SettingsRow label={'Product tour'} description={'Replay the welcome walkthrough'}>
                    <Button
                        size={Const.SM}
                        height={'30px'}
                        fontSize={Const.XS}
                        background={Const.Gray36}
                        color={Const.Gray165}
                        _hover={{ background: Const.Gray69 }}
                        onClick={() => startProductTour({ setAppMode })}
                    >
                        Replay
                    </Button>
                </SettingsRow>
            </SettingsSection>

            {/* Sign out — isolated, away from the settings controls */}
            <VStack align={'stretch'} spacing={Const.SpaceMD} pt={Const.SpaceSM}>
                <Divider borderColor={Const.Gray100} />
                <Button
                    alignSelf={Const.sCenter}
                    variant={'ghost'}
                    size={Const.SM}
                    height={'30px'}
                    fontSize={Const.XS}
                    color={Const.Gray165}
                    _hover={{ background: Const.Red600, color: Const.White }}
                    onClick={handleClickSignOut}
                >
                    Sign out
                </Button>
            </VStack>
        </VStack>

        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay
                backdropFilter={'blur(2px)'}
                bg={'rgba(0, 0, 0, 0.6)'}
            >
            <AlertDialogContent
                bg={Const.Gray31}
                color={Const.White}
            >
                <AlertDialogHeader fontSize={Const.LG}>
                    Cancel subscription
                </AlertDialogHeader>

                <AlertDialogBody>
                    Are you sure? You will loose access to all Pro features.
                </AlertDialogBody>

                <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose} size={Const.SM}>
                    Close
                </Button>
                <Button colorScheme='red' onClick={handleCancelSubscription} ml={3} size={Const.SM}>
                    Cancel subscription
                </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
        </>    
    );
};

export default Profile;
